'use client';

import { type ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { cn } from '@fshr/shared-react';
import { IconTimeSmall } from '../icon';
import BevSafeText from '../bev-safe-text';
import type { ProductAvailabilitySource } from './live-product-data';

type Props = {
	productId?: string;
	productAvailabilityStore?: ProductAvailabilitySource;
	className?: string;
};

const text = (value?: string) => (typeof value === 'string' ? value.trim() : '');

function ExperienceDuration({
	productId,
	productAvailabilityStore,
	className,
}: Props): ReactElement | null {
	if (!productId) return null;

	// Typography class is kept out of cn() - see the note in category-chip.tsx.
	const typographyClass = 'text-body3';
	// Not every product has a duration, and it arrives with the availability fetch. Keep one line of
	// height reserved either way so cards stay aligned and nothing shifts when the data lands.
	const placeholder = (
		<p data-testid="experience-duration-placeholder" aria-hidden="true" className={typographyClass}>
			&nbsp;
		</p>
	);

	if (!productAvailabilityStore || productAvailabilityStore.fetching) return placeholder;

	const summaries = productAvailabilityStore.allProducts?.find(
		product => product.id === productId
	)?.summaries;
	// The authored duration tag wins over the generic summary text, as on the product page hero.
	const label = text(summaries?.durationTag) || text(summaries?.duration?.text);
	if (!label) return placeholder;

	return (
		<p
			data-testid="experience-duration"
			className={`${typographyClass} ${cn('flex items-center gap-060 text-text-secondary', className)}`}
		>
			<span className="flex shrink-0" aria-hidden="true">
				<IconTimeSmall />
			</span>
			<BevSafeText>{label}</BevSafeText>
		</p>
	);
}

export default observer(ExperienceDuration);
